import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';


@Injectable({
  providedIn: 'root'
})
export class MmuWebComponentLoaderService {

  private _requestCache = new Map<string, Observable<any>>();
  private _scriptRegistry = new Map<string, HTMLScriptElement>();

  constructor(private _http: HttpClient) {
  }

  scriptExists(src: string): boolean {
    return this._scriptRegistry.has(src);
  }

  async loadScript(manifestUrl: string, tag: string): Promise<void> {
    // Try to resolve the real, hashed URL by using its manifest
    const url = await this.getScriptUrlFromManifest(manifestUrl, tag);

    if (!url) {
      const msg = `MmuWebComponentLoaderService: No information on the JavaScript file URL found in the manifest for ${tag}.`;
      return Promise.reject(msg);
    }

    if (this.scriptExists(url)) {
      // When this script has already been loaded, skip the rest
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script') as HTMLScriptElement;
      script.src = url;
      script.onload = (): void => {
        resolve();
      };
      script.onerror = (err): void => {
        reject(err);
      };

      this._scriptRegistry.set(url, script);

      if (tag) {
        script.setAttribute('data-web-component-tag-name', tag);
      }

      document.body.appendChild(script);
    });
  }

  private async getScriptUrlFromManifest(manifestUrl: string, key: string): Promise<string | null> {
    if (!manifestUrl) {
      return null;
    }

    if (!this._requestCache.has(manifestUrl)) {
      const req = this._http.get(manifestUrl).pipe(shareReplay({ bufferSize: 1, refCount: false }));
      this._requestCache.set(manifestUrl, req);
    }

    // Try to load manifest and get the real, hashed URL
    try {
      const manifest = await this._requestCache.get(manifestUrl)?.toPromise() as { [key: string]: string };
      return manifest[key] || null;
    } catch (e) {
      console.warn(`MmuWebComponentLoaderService: Could not load manifest from ${manifestUrl}`, e);
      return null;
    }
  }
}
